import axios, { AxiosError } from 'axios';

// Environment-aware API configuration
const API_BASE_URL = process.env.BACKEND_URL || 'http://localhost:4000/api';

// Configure axios defaults
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.withCredentials = true; // Enable credentials for CORS

// Add axios response interceptor for better error handling
axios.interceptors.response.use(
    response => response,
    (error: AxiosError) => {
        if (error.response) {
            // Server responded with a status code outside the 2xx range
            console.error('API Error Response:', error.response.data);
            console.error('Status:', error.response.status);
        } else if (error.request) {
            // Request was made but no response received
            console.error('No response received:', error.request);
        } else {
            // Error setting up the request
            console.error('Error setting up request:', error.message);
        }
        return Promise.reject(error);
    }
);

export interface Guest {
    name: string;
    email: string;
    phone: string;
    date: string;
}

export interface Wish {
    title: string;
    description: string;
    guestId: number;
}

export interface Location {
    name: string;
    address: string;
    votes: number;
    location: { lat: number; lng: number };
}

// Guest APIs
export const createGuest = async (guest: Guest) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/guests/guests`, guest);
        return response.data;
    } catch (error) {
        console.error('Error creating guest:', error);
        throw error;
    }
};

export const getGuests = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/guests/guests`);
        return response.data;
    } catch (error) {
        console.error('Error fetching guests:', error);
        throw error;
    }
};

// Wish APIs
export const createWish = async (wish: Wish) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/wishes/wishes`, wish);
        return response.data;
    } catch (error) {
        console.error('Error creating wish:', error);
        throw error;
    }
};

export const getWishes = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/wishes/wishes`);
        return response.data;
    } catch (error) {
        console.error('Error fetching wishes:', error);
        throw error;
    }
};

// Location APIs
export const createLocation = async (location: Location) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/locations/locations`, location);
        return response.data;
    } catch (error) {
        console.error('Error creating location:', error);
        throw error;
    }
};

export const getLocations = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/locations/locations`);
        return response.data;
    } catch (error) {
        console.error('Error fetching locations:', error);
        throw error;
    }
};

export const voteLocation = async (locationId: number) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/locations/locations/${locationId}/vote`);
        return response.data;
    } catch (error) {
        console.error('Error voting for location:', error);
        throw error;
    }
};

// Image APIs
export const uploadImage = async (formData: FormData) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/images/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading image:', error);
        throw error;
    }
};

export const getImages = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/images/images`);
        return response.data;
    } catch (error) {
        console.error('Error fetching images:', error);
        throw error;
    }
};